import { useCallback, useMemo } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Radio from '@mui/material/Radio';
import TextFieldMUI from '@mui/material/TextField';
import { useHandleShowDialogs } from 'forms/hooks';
import { all, includes } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import useFormData from '@gef-ui/form-core/hooks/state/useFormData';
import { validationDisplayTypes } from '@gef-ui/form-material/constants';
import SubmitButton from '@gef-ui/form-material/SubmitButton';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { ComponentLoader } from 'components';
import Agency from 'components/atoms/GefMui/OrganizationStructure/Agency';
import Manager from 'components/atoms/GefMui/OrganizationStructure/Manager';
import { MaxLengths } from 'constants/forms';
import fetch from 'features/common/fetch';
import { EDIT_GUARANTOR_FORM_NAME, FIELDS } from 'features/views/campaignSettings/guarantors/form';
import guarantorsEdit from 'features/views/guarantorsEdit';
import { RADIO_OPTIONS_IDS } from 'features/views/guarantorsEdit/constants';
import { joinString } from 'utils/string';

export const Filter = () => {
	const dispatch = useDispatch();
	const radio = useSelector(guarantorsEdit.selectors.getRadio);
	const guarantors = useSelector(guarantorsEdit.selectors.getGuarantors);
	const selectedGuarantors = useSelector(guarantorsEdit.selectors.getSelectedGuarantors);
	const formData = useFormData();

	const requiredFieldsAreEmpty = useMemo(
		() => all(isNilOrEmpty)([formData?.surname, formData?.sellerIn, formData?.login]),
		[formData?.surname, formData?.sellerIn, formData?.login],
	);

	const renderOption = useCallback((option) => joinString(' ', [option.surname, option.firstName]), []);
	const renderInput = useCallback(
		(params) => <TextFieldMUI {...params} label={getLabelMessage('guarantors:moreThenOne')} />,
		[],
	);

	const handleSelectGuarantor = useCallback(
		(e, values) => {
			const ids = values.map((value) => value.id);
			dispatch(guarantorsEdit.actions.selectGuarantors(ids));
		},
		[dispatch],
	);

	const autocompleteValues = useMemo(
		() => guarantors.filter((item) => includes(item.id, selectedGuarantors)),
		[guarantors, selectedGuarantors],
	);

	const isOptionEqualToValueParse = useCallback((option, value) => option.id === value.id, []);

	const handleRadioChange = useCallback(
		(event) => {
			dispatch(guarantorsEdit.actions.changeRadio(event.target.value));
		},
		[dispatch],
	);

	const handleOrganizationStructureChange = useCallback(() => {
		dispatch(guarantorsEdit.actions.organizationStructureChange());
	}, [dispatch]);

	const handleSubmit = useCallback(() => {
		dispatch(guarantorsEdit.actions.submitForm());
	}, [dispatch]);

	const handleShowDialogForRequiredFields = useHandleShowDialogs({
		page: EDIT_GUARANTOR_FORM_NAME,
		mapper: {
			atLeastOneOfRequiredFields: ['surname', 'sellerIn', 'login'],
		},
		continueFn: handleSubmit,
	});

	return (
		<Grid container>
			<Grid size={12}>
				<Grid container spacing={2}>
					<Grid size={4}>
						<Grid container>
							<Grid size={12}>
								<FormControlLabel
									control={
										<Radio
											onChange={handleRadioChange}
											value={RADIO_OPTIONS_IDS.byOrganizeStructure}
											checked={radio === RADIO_OPTIONS_IDS.byOrganizeStructure}
										/>
									}
									label={getLabelMessage('guarantors:byOrganizeStructure')}
								/>
							</Grid>
							<Grid size={12}>
								<FormControlLabel
									control={
										<Radio
											onChange={handleRadioChange}
											value={RADIO_OPTIONS_IDS.outOrganizeStructure}
											checked={radio === RADIO_OPTIONS_IDS.outOrganizeStructure}
										/>
									}
									label={getLabelMessage('guarantors:outOrganizeStructure')}
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid size={8}>
						<Grid container spacing={2}>
							{radio === RADIO_OPTIONS_IDS.byOrganizeStructure ? (
								<>
									<Grid size={6}>
										<Agency
											fieldName={FIELDS.agency}
											formName={EDIT_GUARANTOR_FORM_NAME}
											disableOnOneOptions
											autoSelect
											onChange={handleOrganizationStructureChange}
										/>
									</Grid>
									<Grid size={6}>
										<Manager
											fieldName={FIELDS.manager}
											formName={EDIT_GUARANTOR_FORM_NAME}
											disableOnOneOptions
											autoSelect
											onChange={handleOrganizationStructureChange}
										/>
									</Grid>
								</>
							) : (
								<>
									<Grid size={6}>
										<TextField
											size="small"
											fieldName={FIELDS.surname}
											label={getLabelMessage('guarantors:surname')}
											required={
												radio === RADIO_OPTIONS_IDS.outOrganizeStructure &&
												requiredFieldsAreEmpty
											}
											validationDisplay={
												!formData?.surname ? validationDisplayTypes.HIDDEN : undefined
											}
											slotProps={{
												htmlInput: {
													maxLength: MaxLengths.SURNAME,
												},
											}}
										/>
									</Grid>
									<Grid size={6}>
										<TextField
											size="small"
											fieldName={FIELDS.firstName}
											label={getLabelMessage('guarantors:firstName')}
											slotProps={{
												htmlInput: {
													maxLength: MaxLengths.NAME,
												},
											}}
										/>
									</Grid>
									<Grid size={6}>
										<TextField
											size="small"
											fieldName={FIELDS.sellerIn}
											label={getLabelMessage('guarantors:sellerIn')}
											required={
												radio === RADIO_OPTIONS_IDS.outOrganizeStructure &&
												requiredFieldsAreEmpty
											}
											validationDisplay={
												!formData?.sellerIn ? validationDisplayTypes.HIDDEN : undefined
											}
											slotProps={{
												htmlInput: {
													maxLength: MaxLengths.SELLER_IN,
												},
											}}
										/>
									</Grid>
									<Grid size={6}>
										<TextField
											size="small"
											fieldName={FIELDS.login}
											label={getLabelMessage('guarantors:login')}
											required={
												radio === RADIO_OPTIONS_IDS.outOrganizeStructure &&
												requiredFieldsAreEmpty
											}
											validationDisplay={
												!formData?.login ? validationDisplayTypes.HIDDEN : undefined
											}
											slotProps={{
												htmlInput: {
													maxLength: MaxLengths.LOGIN,
												},
											}}
										/>
									</Grid>
								</>
							)}
						</Grid>
					</Grid>
					{radio === RADIO_OPTIONS_IDS.byOrganizeStructure ? (
						<Grid size={12}>
							<ComponentLoader fetchStorePath={fetch.constants.urls.campaignGuarantorCandidate}>
								<Autocomplete
									size="small"
									fullWidth
									fieldName={FIELDS.guarantors}
									multiple
									label={getLabelMessage('guarantors:moreThenOne')}
									options={guarantors}
									getOptionLabel={renderOption}
									disabled={isNilOrEmpty(guarantors)}
									renderInput={renderInput}
									onChange={handleSelectGuarantor}
									value={autocompleteValues}
									isOptionEqualToValue={isOptionEqualToValueParse}
								/>
							</ComponentLoader>
						</Grid>
					) : (
						<>
							<Grid size={8} />
							<Grid size={4}>
								<SubmitButton
									variant="contained"
									onClick={handleSubmit}
									onClickError={handleShowDialogForRequiredFields}
									fullWidth
									text={getLabelMessage('common:searchButton')}
								/>
							</Grid>
						</>
					)}
				</Grid>
			</Grid>
		</Grid>
	);
};

export default Filter;
